/**
 * Receivables Page - Contas a Receber (Facturas Pendentes)
 * 
 * Lista facturas em dívida, Aging Report e Notas de Crédito
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { settingsAPI, getErrorMessage, printAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { ModulePageHero } from '@/components/common/ModulePageHero';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { ResizableTableHandle } from '@/components/common/ResizableTableHandle';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { useResizableTableColumns } from '@/hooks/useResizableTableColumns';
import { safeRequest } from '@/lib/safeRequest';
import { COMPACT_ACTION_BUTTONS, TABLE_DENSITY } from '@/lib/tableDensity';
import { getStoredAuthToken } from '@/lib/authStorage';
import { applyCleanOrLegacyParam, buildHandleMaps, resolveHandleOrLegacyId } from '@/lib/queryHandles';
import { 
  FileText, Receipt, AlertTriangle, Clock, CheckCircle, 
  DollarSign, Search, Download, Filter, Calendar, 
  CreditCard, RefreshCcw, TrendingDown, BarChart3
} from 'lucide-react';
import { formatCurrency, formatDateTime, formatDisplayOnlyCode, cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RECEIVABLE_TABS = ['facturas', 'aging', 'credit-notes', 'recibos'];
const normalizeReceivableText = (value) => (value || '').toString().toLowerCase();
const RECEIVABLES_TABLE_CLASS = TABLE_DENSITY.table;
const RECEIVABLES_INVOICES_WIDTHS = {
  document: 132,
  customer: 166,
  date: 104,
  due: 116,
  total: 96,
  paid: 96,
  balance: 102,
  status: 104,
  actions: 198,
};
const RECEIVABLES_CREDIT_NOTES_WIDTHS = {
  number: 108,
  original: 120,
  customer: 154,
  reason: 180,
  amount: 98,
  refund: 110,
  status: 98,
  date: 100,
  actions: 112,
};
const RECEIVABLES_RECEIPTS_WIDTHS = {
  receipt: 120,
  invoice: 120,
  customer: 154,
  date: 100,
  amount: 96,
  payment: 132,
  actions: 122,
};

const fetchAuthenticatedJson = async (path) => {
  const token = getStoredAuthToken();
  const response = await fetch(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.detail || `Request failed: ${response.status}`);
  }

  return data;
};

export default function ReceivablesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getColumnStyle: getInvoiceColumnStyle, startColumnResize: startInvoiceColumnResize } = useResizableTableColumns({
    storageKey: 'cg-receivables-invoices-columns',
    defaultWidths: RECEIVABLES_INVOICES_WIDTHS,
    minWidth: 68,
  });
  const { getColumnStyle: getCreditNoteColumnStyle, startColumnResize: startCreditNoteColumnResize } = useResizableTableColumns({
    storageKey: 'cg-receivables-credit-notes-columns',
    defaultWidths: RECEIVABLES_CREDIT_NOTES_WIDTHS,
    minWidth: 68,
  });
  const { getColumnStyle: getReceiptColumnStyle, startColumnResize: startReceiptColumnResize } = useResizableTableColumns({
    storageKey: 'cg-receivables-receipts-columns',
    defaultWidths: RECEIVABLES_RECEIPTS_WIDTHS,
    minWidth: 68,
  });
  const [facturas, setFacturas] = useState([]);
  const [recibos, setRecibos] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [agingReport, setAgingReport] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [locations, setLocations] = useState([]);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  
  // Active tab
  const initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(RECEIVABLE_TABS.includes(initialTab) ? initialTab : 'facturas');
  
  // Receipt dialog
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);
  const [receiptForm, setReceiptForm] = useState({
    amount: 0,
    payment_account_id: '',
    payment_account_name: '',
    reference: '',
    notes: ''
  });
  const [processingReceipt, setProcessingReceipt] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [locationFilter, setLocationFilter] = useState(searchParams.get('location_id') || 'all');
  const receivablesSyncSnapshotRef = useRef('');
  const requestedLocationHandle = searchParams.get('location');
  const locationHandleMaps = useMemo(() => buildHandleMaps(locations, (location) => location.name), [locations]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const saleRef = searchParams.get('sale') || searchParams.get('sale_number') || searchParams.get('sale_id');
    const nextSearch = searchParams.get('q') || saleRef || '';
    const nextStatus = searchParams.get('status') || 'all';
    const nextLocation = resolveHandleOrLegacyId({
      cleanValue: searchParams.get('location'),
      legacyValue: searchParams.get('location_id') || 'all',
      handleToId: locationHandleMaps.handleToId,
    }) || 'all';
    const nextTabRaw = searchParams.get('tab');
    const nextTab = RECEIVABLE_TABS.includes(nextTabRaw) ? nextTabRaw : 'facturas';
    const snapshot = `${nextTab}|${nextSearch}|${nextStatus}|${nextLocation}`;

    if (receivablesSyncSnapshotRef.current === snapshot) return;
    receivablesSyncSnapshotRef.current = snapshot;

    setSearchTerm((prev) => (prev === nextSearch ? prev : nextSearch));
    setStatusFilter((prev) => (prev === nextStatus ? prev : nextStatus));
    setLocationFilter((prev) => {
      if (!searchParams.get('location') && !searchParams.get('location_id') && prev !== 'all') {
        return prev;
      }
      return prev === nextLocation ? prev : nextLocation;
    });
    setActiveTab((prev) => (prev === nextTab ? prev : nextTab));
  }, [searchParams, locationHandleMaps.handleToId]);

  useEffect(() => {
    if (!requestedLocationHandle || !locations.length) return;
    const resolvedLocationId = resolveHandleOrLegacyId({
      cleanValue: requestedLocationHandle,
      legacyValue: searchParams.get('location_id') || 'all',
      handleToId: locationHandleMaps.handleToId,
    });

    if (resolvedLocationId && locationFilter === 'all') {
      setLocationFilter(resolvedLocationId);
    }
  }, [locationFilter, locationHandleMaps.handleToId, locations.length, requestedLocationHandle, searchParams]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', activeTab);

    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch) params.set('q', trimmedSearch);
    else if (!searchParams.get('sale') && !searchParams.get('sale_id') && !searchParams.get('sale_number')) params.delete('q');

    params.delete('sale');
    if (searchParams.get('sale_number')) params.set('sale', searchParams.get('sale_number'));
    else if (searchParams.get('sale')) params.set('sale', searchParams.get('sale'));
    else if (searchParams.get('sale_id')) params.set('sale_id', searchParams.get('sale_id'));
    params.delete('sale_number');

    if (statusFilter !== 'all') params.set('status', statusFilter);
    else params.delete('status');

    params.delete('location');
    params.delete('location_id');
    if (locationFilter !== 'all') {
      const hasLocationHandles = Object.keys(locationHandleMaps.idToHandle || {}).length > 0;
      if (!hasLocationHandles && searchParams.get('location_id') === locationFilter) {
        params.set('location_id', locationFilter);
      } else {
        applyCleanOrLegacyParam({
          params,
          cleanKey: 'location',
          legacyKey: 'location_id',
          value: locationFilter,
          idToHandle: locationHandleMaps.idToHandle,
        });
      }
    }

    const stateSnapshot = `${activeTab}|${trimmedSearch || ''}|${statusFilter}|${locationFilter}`;

    if (params.toString() !== searchParams.toString()) {
      receivablesSyncSnapshotRef.current = stateSnapshot;
      setSearchParams(params, { replace: true });
    }
  }, [activeTab, searchTerm, statusFilter, locationFilter, searchParams, setSearchParams, locationHandleMaps.idToHandle]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      resetFetchTelemetry();

      const [arRes, recibosRes, resumoRes, accountsRes, agingRes, creditNotesRes, locationsRes] = await Promise.all([
        safeRequest(() => fetchAuthenticatedJson('/api/fiscal/ar'), { items: [], summary: { total: 0, balance: 0, count: 0 } }, { telemetry: { module: 'receivables', endpoint: 'fiscal.ar', onEvent: captureFetchTelemetry } }),
        safeRequest(() => fetchAuthenticatedJson('/api/fiscal/recibos'), [], { telemetry: { module: 'receivables', endpoint: 'fiscal.recibos', onEvent: captureFetchTelemetry } }),
        safeRequest(() => fetchAuthenticatedJson('/api/fiscal/resumo-fiscal?period=this_month'), null, { telemetry: { module: 'receivables', endpoint: 'fiscal.resumo', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listPaymentAccounts(), { data: [] }, { telemetry: { module: 'receivables', endpoint: 'settings.payment_accounts', onEvent: captureFetchTelemetry } }),
        safeRequest(() => fetchAuthenticatedJson('/api/fiscal/aging-report'), null, { telemetry: { module: 'receivables', endpoint: 'fiscal.aging', onEvent: captureFetchTelemetry } }),
        safeRequest(() => fetchAuthenticatedJson('/api/fiscal/notas-credito'), [], { telemetry: { module: 'receivables', endpoint: 'fiscal.credit_notes', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listLocations(), { data: [] }, { telemetry: { module: 'receivables', endpoint: 'settings.locations', onEvent: captureFetchTelemetry } })
      ]);
      
      setFacturas(arRes.items || []);
      setRecibos(recibosRes || []);
      setResumo({
        ...resumoRes,
        facturas: {
          total: arRes.summary?.total || 0,
          pendente: arRes.summary?.balance || 0,
          count: arRes.summary?.count || 0,
        }
      });
      setPaymentAccounts(accountsRes.data?.filter(a => a.is_active && !a.is_deleted) || []);
      setAgingReport(agingRes);
      setCreditNotes(creditNotesRes || []);
      setLocations(locationsRes.data || []);
      
    } catch (error) {
      console.error('Error loading receivables:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const hasReceivablesData = Boolean(facturas.length || recibos.length || creditNotes.length || agingReport || resumo);
  
  const openReceiptDialog = (factura) => {
    setSelectedFactura(factura);
    setReceiptForm({
      amount: factura.balance_due || 0,
      payment_account_id: '',
      payment_account_name: '',
      reference: '',
      notes: ''
    });
    setReceiptDialogOpen(true);
  };
  
  const handleCreateReceipt = async () => {
    if (!receiptForm.payment_account_id) {
      toast.error('Seleccione a conta de pagamento');
      return;
    }
    
    if (receiptForm.amount <= 0) {
      toast.error('Valor deve ser maior que zero');
      return;
    }
    
    if (receiptForm.amount > selectedFactura.balance_due) {
      toast.error('Valor não pode exceder o saldo devedor');
      return;
    }
    
    setProcessingReceipt(true);
    try {
      const token = getStoredAuthToken();
      
      const account = paymentAccounts.find(a => a.id === receiptForm.payment_account_id);
      
      const response = await fetch(`${API_URL}/api/fiscal/ar/${selectedFactura.sale_id}/payments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: receiptForm.amount,
          payment_account_id: receiptForm.payment_account_id,
          payment_account_name: account?.name || '',
          payment_method_id: account?.payment_method_id || null,
          payment_method_name: account?.payment_method_name || account?.name || '',
          reference: receiptForm.reference,
          notes: receiptForm.notes
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Erro ao criar recibo');
      }
      
      toast.success(`Recibo ${formatDisplayOnlyCode(result.receipt_number, 'REC')} emitido com sucesso!`);
      
      // Open PDF via Template Builder
      await printAPI.renderDocumentPdf({
        doc_type: 'receipt',
        sale_id: selectedFactura?.sale_id,
        receipt_id: result.receipt_id,
        location_id: selectedFactura?.location_id,
        module_group: 'CONTAS_A_RECEBER',
        copy_type: 'Original'
      }, 'preview');
      
      setReceiptDialogOpen(false);
      loadData();
      
    } catch (error) {
      toast.error(error.message || 'Erro ao emitir recibo');
    } finally {
      setProcessingReceipt(false);
    }
  };
  
  const downloadFacturaPDF = async (saleId, copyType = 'Original') => {
    try {
      const result = await printAPI.renderDocumentPdf({
        doc_type: 'invoice',
        sale_id: saleId,
        module_group: 'VENDAS',
        copy_type: copyType
      }, 'preview');
      if (!result.success) {
        toast.error(result.error || 'Erro ao gerar factura');
      }
    } catch (error) {
      toast.error('Erro ao gerar factura');
    }
  };

  const downloadReciboPDF = async (reciboId, copyType = 'Original') => {
    try {
      const result = await printAPI.renderDocumentPdf({
        doc_type: 'receipt',
        receipt_id: reciboId,
        module_group: 'CONTAS_A_RECEBER',
        copy_type: copyType
      }, 'preview');
      if (!result.success) {
        toast.error(result.error || 'Erro ao gerar recibo');
      }
    } catch (error) {
      toast.error('Erro ao gerar recibo');
    }
  };

  const downloadCreditNotePDF = async (creditNoteId, copyType = 'Original') => {
    try {
      const result = await printAPI.renderDocumentPdf({
        doc_type: 'credit_note',
        credit_note_id: creditNoteId,
        module_group: 'VENDAS',
        copy_type: copyType
      }, 'preview');
      if (!result.success) {
        toast.error(result.error || 'Erro ao gerar nota de crédito');
      }
    } catch (error) {
      toast.error('Erro ao gerar nota de crédito');
    }
  };
  
  // Filter facturas
  const filteredFacturas = useMemo(() => facturas.filter((factura) => {
    if (locationFilter !== 'all' && factura.location_id !== locationFilter) return false;
    if (statusFilter !== 'all' && (factura.status || '').toUpperCase() !== statusFilter.toUpperCase()) return false;
    if (searchTerm) {
      const term = normalizeReceivableText(searchTerm);
      return [
        factura.doc_number,
        factura.customer_name,
        factura.sale_number,
        factura.sale_id,
      ].some((field) => normalizeReceivableText(field).includes(term));
    }
    return true;
  }), [facturas, locationFilter, statusFilter, searchTerm]);

  const filteredRecibos = useMemo(() => recibos.filter((recibo) => {
    if (locationFilter !== 'all' && recibo.location_id !== locationFilter) return false;
    if (!searchTerm) return true;
    const term = normalizeReceivableText(searchTerm);
    return [
      recibo.receipt_number,
      recibo.invoice_number,
      recibo.customer_name,
      recibo.sale_number,
    ].some((field) => normalizeReceivableText(field).includes(term));
  }), [recibos, locationFilter, searchTerm]);

  const filteredCreditNotes = useMemo(() => creditNotes.filter((creditNote) => {
    if (locationFilter !== 'all' && creditNote.location_id !== locationFilter) return false;
    if (!searchTerm) return true;
    const term = normalizeReceivableText(searchTerm);
    return [
      creditNote.nc_number,
      creditNote.original_document_number,
      creditNote.customer_name,
      creditNote.reason,
    ].some((field) => normalizeReceivableText(field).includes(term));
  }), [creditNotes, locationFilter, searchTerm]);
  
  const getStatusBadge = (status) => {
    switch ((status || '').toUpperCase()) {
      case 'ABERTO':
        return <Badge variant="outline" className="border-red-500 text-red-600">ABERTO</Badge>;
      case 'PARCIAL':
        return <Badge variant="outline" className="border-amber-500 text-amber-600">PARCIAL</Badge>;
      case 'PAGO':
        return <Badge variant="outline" className="border-green-500 text-green-600">PAGO</Badge>;
      case 'VENCIDO':
        return <Badge variant="destructive">VENCIDO</Badge>;
      default:
        return <Badge variant="secondary">{status || 'N/A'}</Badge>;
    }
  };

  return (
    <ModuleStateBoundary
      loading={loading}
      hasData={hasReceivablesData}
      partialIssues={partialIssues}
      loadingTestId="receivables-page-loading"
      partialIssuesTestId="receivables-page-partial-issues"
      emptyState={
        <div className="space-y-6" data-testid="receivables-page">
          <EmptyStatePanel
            title="Sem dados disponíveis"
            description="Nenhum registo encontrado para Contas a Receber. Pode configurar ou importar dados para começar."
            testId="receivables-page-empty-state"
          />
        </div>
      }
    >
    <div className="space-y-4" data-testid="receivables-page">
      <ModulePageHero
        testId="receivables-page-hero"
        title="Contas a Receber"
        description="Gestão financeira com foco em facturas pendentes, recibos emitidos e aging report no mesmo cockpit visual." 
        icon={Receipt}
        badge="Financeiro"
        compact
        stats={resumo ? [
          { label: 'Vendas a Dinheiro', value: formatCurrency(resumo.vendas_dinheiro?.total || 0), helper: `${resumo.vendas_dinheiro?.count || 0} documentos` },
          { label: 'Facturas Emitidas', value: formatCurrency(resumo.facturas?.total || 0), helper: `${resumo.facturas?.count || 0} documentos` },
          { label: 'Facturas Pendentes', value: formatCurrency(resumo.facturas?.pendente || 0), helper: `${filteredFacturas.length} por liquidar` },
          { label: 'Recibos Emitidos', value: formatCurrency(recibos.reduce((sum, r) => sum + (r.amount || 0), 0)), helper: `${recibos.length} recibos` },
        ] : []}
      />

      {/* Summary Cards */}
      {resumo && false && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Vendas a Dinheiro</p>
                  <p className="text-xl font-bold">{formatCurrency(resumo.vendas_dinheiro?.total || 0)}</p>
                  <p className="text-xs text-muted-foreground">{resumo.vendas_dinheiro?.count || 0} documentos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Facturas Emitidas</p>
                  <p className="text-xl font-bold">{formatCurrency(resumo.facturas?.total || 0)}</p>
                  <p className="text-xs text-muted-foreground">{resumo.facturas?.count || 0} documentos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Facturas Pendentes</p>
                  <p className="text-xl font-bold text-amber-600">{formatCurrency(resumo.facturas?.pendente || 0)}</p>
                  <p className="text-xs text-muted-foreground">{filteredFacturas.length} por liquidar</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Receipt className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Recibos Emitidos</p>
                  <p className="text-xl font-bold">{formatCurrency(resumo.recibos?.total || 0)}</p>
                  <p className="text-xs text-muted-foreground">{resumo.recibos?.count || 0} recibos</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-2.5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0" data-testid="receivables-tabs-list">
            <TabsTrigger value="facturas" className="h-9 rounded-full border border-slate-200 bg-white px-3 text-xs shadow-sm data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="receivables-tab-facturas-trigger">
            Facturas
            </TabsTrigger>
            <TabsTrigger value="aging" className="h-9 rounded-full border border-slate-200 bg-white px-3 text-xs shadow-sm data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="receivables-tab-aging-trigger">
            Aging Report
            </TabsTrigger>
            <TabsTrigger value="credit-notes" className="h-9 rounded-full border border-slate-200 bg-white px-3 text-xs shadow-sm data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="receivables-tab-credit-notes-trigger">
            Notas Crédito
            </TabsTrigger>
            <TabsTrigger value="recibos" className="h-9 rounded-full border border-slate-200 bg-white px-3 text-xs shadow-sm data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="receivables-tab-recibos-trigger">
            Recibos
            </TabsTrigger>
          </TabsList>

          <div className="flex flex-wrap items-center gap-2" data-testid="receivables-context-controls-row">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Pesquisar documentos, cliente, venda"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="h-9 w-64 pl-9 text-sm"
                data-testid="receivables-context-search-input"
              />
            </div>

            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="h-9 w-48 text-sm" data-testid="receivables-context-location-select">
                <SelectValue placeholder="Filtrar localização" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas localizações</SelectItem>
                {locations.map((location) => (
                  <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-36 text-sm" data-testid="receivables-context-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="ABERTO">ABERTO</SelectItem>
                <SelectItem value="PARCIAL">PARCIAL</SelectItem>
                <SelectItem value="PAGO">PAGO</SelectItem>
                <SelectItem value="VENCIDO">VENCIDO</SelectItem>
              </SelectContent>
            </Select>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate(`/documents${locationFilter !== 'all' ? `?location_id=${locationFilter}` : ''}`)}
              data-testid="receivables-open-documents-button"
            >
              Abrir Documentos
            </Button>
          </div>
        </div>
        
        {/* Aging Report Tab */}
        <TabsContent value="aging">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="px-3 py-2.5">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="h-5 w-5 text-amber-600" />
                    Aging Report - Relatório de Vencimentos
                  </CardTitle>
                  <CardDescription>Análise de dívidas por período de atraso</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={loadData}>
                  <RefreshCcw className="h-4 w-4 mr-2" />
                  Actualizar
                </Button>
              </div>
            </CardHeader>
                <CardContent className="p-3 pt-0">
              {agingReport && (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Card className="border-green-200 bg-green-50 shadow-none">
                      <CardContent className="p-3">
                        <p className="text-sm font-medium text-green-700">Total em Dívida</p>
                        <p className="text-2xl font-bold text-green-800">
                          {formatCurrency(agingReport.summary?.total_due || 0)}
                        </p>
                        <p className="text-xs text-green-600">
                          {agingReport.summary?.total_facturas || 0} facturas
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="border-blue-200 bg-blue-50 shadow-none">
                      <CardContent className="p-3">
                        <p className="text-sm font-medium text-blue-700">Dentro do Prazo</p>
                        <p className="text-2xl font-bold text-blue-800">
                          {formatCurrency(agingReport.summary?.total_current || 0)}
                        </p>
                        <p className="text-xs text-blue-600">
                          {agingReport.aging?.current?.count || 0} facturas
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="border-amber-200 bg-amber-50 shadow-none">
                      <CardContent className="p-3">
                        <p className="text-sm font-medium text-amber-700">Em Atraso</p>
                        <p className="text-2xl font-bold text-amber-800">
                          {formatCurrency(agingReport.summary?.total_overdue || 0)}
                        </p>
                        <p className="text-xs text-amber-600">
                          {(agingReport.aging?.['1_30']?.count || 0) + 
                           (agingReport.aging?.['31_60']?.count || 0) + 
                           (agingReport.aging?.['61_90']?.count || 0) + 
                           (agingReport.aging?.['90_plus']?.count || 0)} facturas
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="border-red-200 bg-red-50 shadow-none">
                      <CardContent className="p-3">
                        <p className="text-sm font-medium text-red-700">Crítico (+90 dias)</p>
                        <p className="text-2xl font-bold text-red-800">
                          {formatCurrency(agingReport.aging?.['90_plus']?.total || 0)}
                        </p>
                        <p className="text-xs text-red-600">
                          {agingReport.aging?.['90_plus']?.count || 0} facturas
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Aging Breakdown */}
                  <div className="space-y-4">
                    {[
                      { key: 'current', label: 'Dentro do Prazo', color: 'bg-green-500', textColor: 'text-green-700' },
                      { key: '1_30', label: '1-30 dias em atraso', color: 'bg-yellow-500', textColor: 'text-yellow-700' },
                      { key: '31_60', label: '31-60 dias em atraso', color: 'bg-orange-500', textColor: 'text-orange-700' },
                      { key: '61_90', label: '61-90 dias em atraso', color: 'bg-red-400', textColor: 'text-red-600' },
                      { key: '90_plus', label: '+90 dias em atraso', color: 'bg-red-600', textColor: 'text-red-800' },
                    ].map(({ key, label, color, textColor }) => {
                      const bucket = agingReport.aging?.[key] || { count: 0, total: 0, facturas: [] };
                      const percentage = agingReport.summary?.total_due > 0 
                        ? (bucket.total / agingReport.summary.total_due) * 100 
                        : 0;
                      
                      return (
                        <div key={key} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className={cn("font-medium", textColor)}>{label}</span>
                            <div className="text-right">
                              <span className="font-bold">{formatCurrency(bucket.total)}</span>
                              <span className="text-sm text-muted-foreground ml-2">
                                ({bucket.count} facturas)
                              </span>
                            </div>
                          </div>
                          <Progress value={percentage} className={cn("h-2", `[&>div]:${color}`)} />
                          
                          {/* Expandable list of invoices */}
                          {bucket.facturas?.length > 0 && bucket.count <= 5 && (
                            <div className="pl-4 space-y-1">
                              {bucket.facturas.map(f => (
                                <div key={f.id} className="flex justify-between text-sm text-muted-foreground">
                                  <span>{f.number} - {f.customer_name}</span>
                                  <span>{formatCurrency(f.balance_due)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Credit Notes Tab */}
        <TabsContent value="credit-notes">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <RefreshCcw className="h-5 w-5 text-purple-600" />
                    Notas de Crédito
                  </CardTitle>
                  <CardDescription>Gestão de devoluções e créditos</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[520px]" data-testid="credit-notes-table-wrapper">
              <Table className={RECEIVABLES_TABLE_CLASS}>
                <TableHeader className="sticky top-0 z-10 bg-white">
                  <TableRow>
                    <TableHead className="relative" style={getCreditNoteColumnStyle('number')}>Nº NC<ResizableTableHandle onMouseDown={(event) => startCreditNoteColumnResize('number', event)} testId="credit-note-column-resize-number" /></TableHead>
                    <TableHead className="relative" style={getCreditNoteColumnStyle('original')}>Doc. Original<ResizableTableHandle onMouseDown={(event) => startCreditNoteColumnResize('original', event)} testId="credit-note-column-resize-original" /></TableHead>
                    <TableHead className="relative" style={getCreditNoteColumnStyle('customer')}>Cliente<ResizableTableHandle onMouseDown={(event) => startCreditNoteColumnResize('customer', event)} testId="credit-note-column-resize-customer" /></TableHead>
                    <TableHead className="relative" style={getCreditNoteColumnStyle('reason')}>Motivo<ResizableTableHandle onMouseDown={(event) => startCreditNoteColumnResize('reason', event)} testId="credit-note-column-resize-reason" /></TableHead>
                    <TableHead className="relative text-right" style={getCreditNoteColumnStyle('amount')}>Valor<ResizableTableHandle onMouseDown={(event) => startCreditNoteColumnResize('amount', event)} testId="credit-note-column-resize-amount" /></TableHead>
                    <TableHead className="relative" style={getCreditNoteColumnStyle('refund')}>Reembolso<ResizableTableHandle onMouseDown={(event) => startCreditNoteColumnResize('refund', event)} testId="credit-note-column-resize-refund" /></TableHead>
                    <TableHead className="relative" style={getCreditNoteColumnStyle('status')}>Estado<ResizableTableHandle onMouseDown={(event) => startCreditNoteColumnResize('status', event)} testId="credit-note-column-resize-status" /></TableHead>
                    <TableHead className="relative" style={getCreditNoteColumnStyle('date')}>Data<ResizableTableHandle onMouseDown={(event) => startCreditNoteColumnResize('date', event)} testId="credit-note-column-resize-date" /></TableHead>
                    <TableHead className="relative" style={getCreditNoteColumnStyle('actions')}>Acções<ResizableTableHandle onMouseDown={(event) => startCreditNoteColumnResize('actions', event)} testId="credit-note-column-resize-actions" /></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCreditNotes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                        Nenhuma nota de crédito emitida
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCreditNotes.map((nc) => (
                      <TableRow key={nc.id}>
                        <TableCell className="font-medium" style={getCreditNoteColumnStyle('number')}>{formatDisplayOnlyCode(nc.nc_number, 'NC')}</TableCell>
                        <TableCell style={getCreditNoteColumnStyle('original')}>{formatDisplayOnlyCode(nc.original_document_number, 'FAC')}</TableCell>
                        <TableCell style={getCreditNoteColumnStyle('customer')}>{nc.customer_name || 'N/A'}</TableCell>
                        <TableCell className="max-w-[150px] truncate" title={nc.reason} style={getCreditNoteColumnStyle('reason')}>
                          {nc.reason}
                        </TableCell>
                        <TableCell className="text-right font-bold text-purple-600" style={getCreditNoteColumnStyle('amount')}>
                          {formatCurrency(nc.total_amount)}
                        </TableCell>
                        <TableCell style={getCreditNoteColumnStyle('refund')}>
                          <Badge variant="outline">
                            {nc.refund_method === 'cash' ? 'Numerário' : 
                             nc.refund_method === 'credit' ? 'Crédito' : 'Crédito Loja'}
                          </Badge>
                        </TableCell>
                        <TableCell style={getCreditNoteColumnStyle('status')}>
                          {nc.status === 'pending' && <Badge variant="outline" className="border-amber-500 text-amber-600">Pendente</Badge>}
                          {nc.status === 'approved' && <Badge variant="outline" className="border-blue-500 text-blue-600">Aprovada</Badge>}
                          {nc.status === 'processed' && <Badge variant="outline" className="border-green-500 text-green-600">Processada</Badge>}
                          {nc.status === 'cancelled' && <Badge variant="destructive">Cancelada</Badge>}
                        </TableCell>
                        <TableCell style={getCreditNoteColumnStyle('date')}>{formatDateTime(nc.created_at).split(' ')[0]}</TableCell>
                        <TableCell style={getCreditNoteColumnStyle('actions')}>
                          <div className="flex items-center gap-1 whitespace-nowrap">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className={COMPACT_ACTION_BUTTONS.icon}
                              onClick={() => downloadCreditNotePDF(nc.id, 'Original')}
                              data-testid={`credit-note-pdf-original-${nc.id}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className={COMPACT_ACTION_BUTTONS.tab}
                              onClick={() => downloadCreditNotePDF(nc.id, 'Duplicado')}
                              data-testid={`credit-note-pdf-duplicate-${nc.id}`}
                            >
                              Dup
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="facturas">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Contas a Receber</CardTitle>
                  <CardDescription>Vendas a prazo com controlo de saldos e recibos</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={loadData} data-testid="receivables-refresh-data-button">
                  <RefreshCcw className="h-4 w-4 mr-2" />
                  Actualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[520px]" data-testid="receivables-invoices-table-wrapper">
              <Table className="text-[11px] leading-tight">
                <TableHeader className="sticky top-0 z-10 bg-white">
                  <TableRow>
                    <TableHead className="relative" style={getInvoiceColumnStyle('document')}>Nº Documento<ResizableTableHandle onMouseDown={(event) => startInvoiceColumnResize('document', event)} testId="receivables-column-resize-document" /></TableHead>
                    <TableHead className="relative" style={getInvoiceColumnStyle('customer')}>Cliente<ResizableTableHandle onMouseDown={(event) => startInvoiceColumnResize('customer', event)} testId="receivables-column-resize-customer" /></TableHead>
                    <TableHead className="relative" style={getInvoiceColumnStyle('date')}>Data<ResizableTableHandle onMouseDown={(event) => startInvoiceColumnResize('date', event)} testId="receivables-column-resize-date" /></TableHead>
                    <TableHead className="relative" style={getInvoiceColumnStyle('due')}>Vencimento<ResizableTableHandle onMouseDown={(event) => startInvoiceColumnResize('due', event)} testId="receivables-column-resize-due" /></TableHead>
                    <TableHead className="relative text-right" style={getInvoiceColumnStyle('total')}>Total<ResizableTableHandle onMouseDown={(event) => startInvoiceColumnResize('total', event)} testId="receivables-column-resize-total" /></TableHead>
                    <TableHead className="relative text-right" style={getInvoiceColumnStyle('paid')}>Pago<ResizableTableHandle onMouseDown={(event) => startInvoiceColumnResize('paid', event)} testId="receivables-column-resize-paid" /></TableHead>
                    <TableHead className="relative text-right" style={getInvoiceColumnStyle('balance')}>Saldo<ResizableTableHandle onMouseDown={(event) => startInvoiceColumnResize('balance', event)} testId="receivables-column-resize-balance" /></TableHead>
                    <TableHead className="relative" style={getInvoiceColumnStyle('status')}>Estado<ResizableTableHandle onMouseDown={(event) => startInvoiceColumnResize('status', event)} testId="receivables-column-resize-status" /></TableHead>
                    <TableHead className="relative" style={getInvoiceColumnStyle('actions')}>Acções<ResizableTableHandle onMouseDown={(event) => startInvoiceColumnResize('actions', event)} testId="receivables-column-resize-actions" /></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredFacturas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                        <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                        Nenhuma factura pendente
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredFacturas.map((factura) => (
                      <TableRow key={factura.id} className="hover:bg-slate-50/70" data-testid={`factura-row-${factura.id}`}>
                        <TableCell className="font-medium" style={getInvoiceColumnStyle('document')}>{formatDisplayOnlyCode(factura.doc_number, 'FAC') || 'N/A'}</TableCell>
                        <TableCell style={getInvoiceColumnStyle('customer')}>{factura.customer_name || 'N/A'}</TableCell>
                        <TableCell style={getInvoiceColumnStyle('date')}>{formatDateTime(factura.created_at).split(' ')[0]}</TableCell>
                        <TableCell style={getInvoiceColumnStyle('due')}>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {factura.due_date ? formatDateTime(factura.due_date).split(' ')[0] : 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell className="text-right" style={getInvoiceColumnStyle('total')}>{formatCurrency(factura.total_amount)}</TableCell>
                        <TableCell className="text-right text-green-600" style={getInvoiceColumnStyle('paid')}>{formatCurrency(factura.amount_paid || 0)}</TableCell>
                        <TableCell className="text-right font-bold text-red-600" style={getInvoiceColumnStyle('balance')}>
                          {formatCurrency(factura.balance_due)}
                        </TableCell>
                        <TableCell style={getInvoiceColumnStyle('status')}>{getStatusBadge(factura.status)}</TableCell>
                        <TableCell style={getInvoiceColumnStyle('actions')}>
                          <div className="flex flex-nowrap gap-1 overflow-x-auto">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className={COMPACT_ACTION_BUTTONS.tab}
                              onClick={() => openReceiptDialog(factura)}
                              disabled={(factura.balance_due || 0) <= 0}
                              data-testid={`emit-receipt-btn-${factura.id}`}
                            >
                              <Receipt className="mr-1 h-3.5 w-3.5" />
                              Rec.
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className={COMPACT_ACTION_BUTTONS.tab}
                              onClick={() => {
                                const saleParams = new URLSearchParams({ sale: factura.sale_number || factura.sale_id, tab: 'sales' });
                                if (factura.location_id) saleParams.set('location_id', factura.location_id);
                                if (factura.sale_number) saleParams.set('sale_number', factura.sale_number);
                                window.open(`/sales?${saleParams.toString()}`, '_blank');
                              }}
                              data-testid={`open-sale-from-ar-btn-${factura.id}`}
                            >
                              Venda
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className={COMPACT_ACTION_BUTTONS.tab}
                              onClick={() => downloadFacturaPDF(factura.sale_id, 'Original')}
                              data-testid={`download-factura-original-btn-${factura.id}`}
                            >
                              <Download className="mr-1 h-3.5 w-3.5" />
                              Ori
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-7 px-2 text-[10px]"
                              onClick={() => downloadFacturaPDF(factura.sale_id, 'Duplicado')}
                              data-testid={`download-factura-duplicate-btn-${factura.id}`}
                            >
                              Dup
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="recibos">
          <Card>
            <CardHeader>
              <CardTitle>Recibos Emitidos</CardTitle>
              <CardDescription>Histórico de recibos de liquidação</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[520px]" data-testid="receivables-receipts-table-wrapper">
              <Table className={RECEIVABLES_TABLE_CLASS}>
                <TableHeader className="sticky top-0 z-10 bg-white">
                  <TableRow>
                    <TableHead className="relative" style={getReceiptColumnStyle('receipt')}>Nº Recibo<ResizableTableHandle onMouseDown={(event) => startReceiptColumnResize('receipt', event)} testId="receipts-column-resize-receipt" /></TableHead>
                    <TableHead className="relative" style={getReceiptColumnStyle('invoice')}>Factura<ResizableTableHandle onMouseDown={(event) => startReceiptColumnResize('invoice', event)} testId="receipts-column-resize-invoice" /></TableHead>
                    <TableHead className="relative" style={getReceiptColumnStyle('customer')}>Cliente<ResizableTableHandle onMouseDown={(event) => startReceiptColumnResize('customer', event)} testId="receipts-column-resize-customer" /></TableHead>
                    <TableHead className="relative" style={getReceiptColumnStyle('date')}>Data<ResizableTableHandle onMouseDown={(event) => startReceiptColumnResize('date', event)} testId="receipts-column-resize-date" /></TableHead>
                    <TableHead className="relative text-right" style={getReceiptColumnStyle('amount')}>Valor<ResizableTableHandle onMouseDown={(event) => startReceiptColumnResize('amount', event)} testId="receipts-column-resize-amount" /></TableHead>
                    <TableHead className="relative" style={getReceiptColumnStyle('payment')}>Forma Pag.<ResizableTableHandle onMouseDown={(event) => startReceiptColumnResize('payment', event)} testId="receipts-column-resize-payment" /></TableHead>
                    <TableHead className="relative" style={getReceiptColumnStyle('actions')}>Acções<ResizableTableHandle onMouseDown={(event) => startReceiptColumnResize('actions', event)} testId="receipts-column-resize-actions" /></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecibos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        Nenhum recibo emitido
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRecibos.map((recibo) => (
                      <TableRow key={recibo.id} className="hover:bg-slate-50/70" data-testid={`receivable-receipt-row-${recibo.id}`}>
                        <TableCell className="font-medium" style={getReceiptColumnStyle('receipt')}>{formatDisplayOnlyCode(recibo.receipt_number, 'REC')}</TableCell>
                        <TableCell style={getReceiptColumnStyle('invoice')}>{formatDisplayOnlyCode(recibo.invoice_number, 'INV')}</TableCell>
                        <TableCell style={getReceiptColumnStyle('customer')}>{recibo.customer_name || 'N/A'}</TableCell>
                        <TableCell style={getReceiptColumnStyle('date')}>{formatDateTime(recibo.created_at).split(' ')[0]}</TableCell>
                        <TableCell className="text-right font-bold text-green-600" style={getReceiptColumnStyle('amount')}>
                          {formatCurrency(recibo.amount)}
                        </TableCell>
                        <TableCell data-testid={`receivable-receipt-payment-method-${recibo.id}`} style={getReceiptColumnStyle('payment')}>
                          {recibo.payment_method_name || recibo.payment_account_name || 'N/A'}
                        </TableCell>
                        <TableCell style={getReceiptColumnStyle('actions')}>
                          <div className="flex items-center gap-1 whitespace-nowrap">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className={COMPACT_ACTION_BUTTONS.tab}
                              onClick={() => downloadReciboPDF(recibo.id, 'Original')}
                              data-testid={`download-recibo-original-btn-${recibo.id}`}
                            >
                              <Download className="mr-1 h-3.5 w-3.5" />
                              Ori
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className={COMPACT_ACTION_BUTTONS.tab}
                              onClick={() => downloadReciboPDF(recibo.id, 'Duplicado')}
                              data-testid={`download-recibo-duplicate-btn-${recibo.id}`}
                            >
                              Dup
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Receipt Dialog */}
      <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-purple-600" />
              Emitir Recibo
            </DialogTitle>
            <DialogDescription>
              {selectedFactura?.doc_number}
            </DialogDescription>
          </DialogHeader>
          
          {selectedFactura && (
            <div className="space-y-4">
              {/* Invoice Summary */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Cliente</span>
                  <span>{selectedFactura.customer_name || 'N/A'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total da Factura</span>
                  <span>{formatCurrency(selectedFactura.total_amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Já Pago</span>
                  <span className="text-green-600">{formatCurrency(selectedFactura.amount_paid || 0)}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span>Saldo em Dívida</span>
                  <span className="text-red-600">{formatCurrency(selectedFactura.balance_due)}</span>
                </div>
              </div>
              
              {/* Receipt Form */}
              <div className="space-y-3">
                <div>
                  <Label>Valor a Receber</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={receiptForm.amount}
                    onChange={(e) => setReceiptForm({...receiptForm, amount: parseFloat(e.target.value) || 0})}
                    max={selectedFactura.balance_due}
                    data-testid="receipt-amount-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Máximo: {formatCurrency(selectedFactura.balance_due)}
                  </p>
                </div>
                
                <div>
                  <Label>Conta de Pagamento *</Label>
                  <Select 
                    value={receiptForm.payment_account_id}
                    onValueChange={(v) => setReceiptForm({...receiptForm, payment_account_id: v})}
                  >
                    <SelectTrigger data-testid="receipt-payment-account-select">
                      <SelectValue placeholder="Seleccione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentAccounts.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Referência (Opcional)</Label>
                  <Input
                    value={receiptForm.reference}
                    onChange={(e) => setReceiptForm({...receiptForm, reference: e.target.value})}
                    placeholder="Nº cheque, comprovativo, etc."
                  />
                </div>
                
                <div>
                  <Label>Notas (Opcional)</Label>
                  <Input
                    value={receiptForm.notes}
                    onChange={(e) => setReceiptForm({...receiptForm, notes: e.target.value})}
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiptDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateReceipt}
              disabled={processingReceipt}
              data-testid="confirm-receipt-btn"
            >
              {processingReceipt ? 'A processar...' : 'Emitir Recibo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </ModuleStateBoundary>
  );
}
